import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Camera, Calendar, Lock, CheckCircle2, Sparkles } from "lucide-react";
import { LENSES } from "../data/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "soma_pass_token_v1";

/**
 * Volume II — the 28-day pass experience. Self-contained: fetches its own state from the
 * /api/passes/{token} endpoint, gates today's cup reading + journal capture, and shows
 * a compact library of past days.
 *
 * Triggered by:
 *  - A fresh activation (parent calls onActivated after polling Stripe status)
 *  - Returning visit with a saved pass token in localStorage
 */
export default function VolumeIIPanel({ token, onSignOut }) {
  const [pass, setPass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // today's-reading flow
  const [uploadedB64, setUploadedB64] = useState(null);
  const [uploadedType, setUploadedType] = useState(null);
  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [cupName, setCupName] = useState("");
  const [whatsOnMind, setWhatsOnMind] = useState("");
  const [chosenLens, setChosenLens] = useState(null);
  const [readingLoading, setReadingLoading] = useState(false);
  const [readingError, setReadingError] = useState(null);
  const [todaysReading, setTodaysReading] = useState(null); // { reading, pull_quote, journal_prompts }
  // Day 28 arc keepsake
  const [arcData, setArcData] = useState(null);
  const [arcLoading, setArcLoading] = useState(false);
  const [arcError, setArcError] = useState(null);
  const fileRef = useRef(null);

  const revealArc = async () => {
    setArcLoading(true);
    setArcError(null);
    try {
      const { data } = await axios.get(`${API}/passes/${token}/arc`);
      setArcData(data);
    } catch (err) {
      setArcError(err.response?.data?.detail || err.message || "Couldn't compose your arc yet.");
    } finally {
      setArcLoading(false);
    }
  };

  const loadPass = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/passes/${token}`);
      setPass(data);
      // If the user has completed a reading, fetch that reading's text so we can
      // display it on the "day N complete" card (rather than the next-open day
      // which has no reading yet).
      const dayToShow = data.last_completed_day || null;
      if (dayToShow) {
        try {
          const r = await axios.get(`${API}/passes/${token}/reading/${dayToShow}`);
          setTodaysReading(r.data);
        } catch { /* ok — will just skip content */ }
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Could not load your pass");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) loadPass(); }, [token]);

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      setImageDataUrl(dataUrl);
      const [meta, b64] = dataUrl.split(",");
      const mime = meta.match(/data:([^;]+);/)?.[1] || "image/jpeg";
      setUploadedB64(b64);
      setUploadedType(mime);
    };
    reader.readAsDataURL(file);
  };

  const submitReading = async () => {
    setReadingError(null);
    if (!chosenLens) {
      setReadingError("Pick a focus for today's reading — the cup can be about anything, but Claude reads best with a lens.");
      return;
    }
    setReadingLoading(true);
    try {
      const { data } = await axios.post(`${API}/passes/${token}/reading`, {
        base64Image: uploadedB64,
        mediaType: uploadedType,
        lensInstruction: chosenLens.instruction,
        whatsOnMind: whatsOnMind.trim() || null,
        cupName: cupName.trim() || null,
        lensLabel: chosenLens.label,
      });
      setTodaysReading({
        day: data.day,
        reading: data.reading,
        pull_quote: data.pull_quote,
        journal_prompts: data.journal_prompts,
      });
      setPass(data.state);
    } catch (err) {
      setReadingError(err.response?.data?.detail || err.message || "Could not bring it into focus.");
    } finally {
      setReadingLoading(false);
    }
  };

  const signOut = () => {
    localStorage.removeItem(TOKEN_KEY);
    if (onSignOut) onSignOut();
  };

  if (loading) return <div style={shellStyle}><p style={{ color: "var(--cream-soft)", fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic" }}>opening Volume II…</p></div>;
  if (error || !pass) return (
    <div style={shellStyle}>
      <p style={{ color: "var(--rust)", fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic" }}>{error || "Pass not found."}</p>
      <button type="button" onClick={signOut} className="mono-btn" style={{ marginTop: 14 }}>start over</button>
    </div>
  );

  const daysLeft = Math.max(0, pass.total_days - pass.current_day + (pass.today_completed ? 0 : 1));
  const progressPct = Math.round((pass.readings.length / pass.total_days) * 100);

  return (
    <div data-testid="volume-ii-panel" style={shellStyle}>
      {/* HEADER */}
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <p style={{ margin: 0, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.32em", color: "var(--gold)", textTransform: "uppercase" }}>
          // volume ii — the 28-day practice
        </p>
        <h1 style={{ margin: "10px 0 6px", fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic", fontSize: 38, color: "var(--cream)", fontWeight: 400 }}>
          Day {pass.current_day} <span style={{ color: "var(--dim)", fontSize: 22, fontStyle: "normal" }}>/ {pass.total_days}</span>
        </h1>
        <p style={{ margin: 0, fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.14em", color: "var(--cream-soft)" }}>
          {daysLeft} day{daysLeft === 1 ? "" : "s"} remaining · {pass.readings.length} cup{pass.readings.length === 1 ? "" : "s"} read so far
        </p>
      </div>

      {/* PROGRESS BAR */}
      <div data-testid="volume-ii-progress" style={progressOuterStyle}>
        <div style={{ ...progressInnerStyle, width: `${progressPct}%` }} />
        <span style={progressLabelStyle}>{progressPct}%</span>
      </div>

      {/* TODAY'S STATUS */}
      {pass.today_completed ? (
        <div data-testid="volume-ii-today-done" style={cardStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <CheckCircle2 size={18} style={{ color: "var(--gold)" }} />
            <p style={{ margin: 0, fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--gold)" }}>
              day {pass.last_completed_day || pass.current_day} complete
            </p>
          </div>
          {todaysReading?.pull_quote && (
            <p style={{ margin: "12px 0 14px", fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontSize: 22, color: "var(--cream)", lineHeight: 1.45, textAlign: "center" }}>
              &ldquo;{todaysReading.pull_quote}&rdquo;
            </p>
          )}
          {todaysReading?.reading && (
            <div data-testid="volume-ii-today-reading" style={{ fontFamily: "'IM Fell DW Pica', serif", fontSize: 17, color: "var(--cream)", lineHeight: 1.65, whiteSpace: "pre-wrap" }}>
              {todaysReading.reading}
            </div>
          )}
          <p style={{ margin: "22px 0 0", textAlign: "center", fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic", fontSize: 14, color: "var(--dim)" }}>
            <Lock size={11} style={{ verticalAlign: "middle", marginRight: 6 }} />
            {pass.readings.length >= pass.total_days
              ? "the whole month is yours."
              : `see you tomorrow for day ${Math.min(pass.current_day + 1, pass.total_days)}`}
          </p>
        </div>
      ) : (
        <div data-testid="volume-ii-today-open" style={cardStyle}>
          <p style={{ margin: 0, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--terracotta-soft)" }}>
            <Sparkles size={11} style={{ verticalAlign: "middle", marginRight: 6 }} />
            today&apos;s cup
          </p>
          <p style={{ margin: "8px 0 18px", fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontSize: 19, color: "var(--cream)", lineHeight: 1.5 }}>
            Finish your drink. Photograph the inside. One cup a day, twenty-eight days running.
          </p>

          {!imageDataUrl ? (
            <button
              type="button"
              data-testid="volume-ii-upload-btn"
              className="primary-btn"
              onClick={() => fileRef.current?.click()}
              style={{ width: "100%", padding: "16px 18px", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 10 }}
            >
              <Camera size={16} /> Upload today&apos;s cup
            </button>
          ) : (
            <>
              <img src={imageDataUrl} alt="your cup" data-testid="volume-ii-cup-preview" style={{ width: "100%", maxHeight: 280, objectFit: "cover", marginBottom: 14, border: "1px solid rgba(143,196,224,0.22)" }} />

              {/* Lens picker — required. Grid of chips using the same LENSES as the main journey. */}
              <p style={{ margin: "0 0 8px", fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--terracotta-soft)" }}>
                today&apos;s focus
              </p>
              <div data-testid="volume-ii-lens-picker" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 6, marginBottom: 14 }}>
                {LENSES.map((L) => {
                  const active = chosenLens?.name === L.name;
                  return (
                    <button
                      key={L.name}
                      type="button"
                      data-testid={`volume-ii-lens-${L.name}`}
                      onClick={() => setChosenLens(L)}
                      className={`mono-btn ${active ? "active" : ""}`}
                      style={{ padding: "9px 10px", fontSize: 10, textAlign: "left" }}
                    >
                      {L.label}
                    </button>
                  );
                })}
              </div>

              <input
                data-testid="volume-ii-cup-name"
                value={cupName}
                onChange={(e) => setCupName(e.target.value)}
                placeholder="give today's cup a name (optional)"
                style={inputStyle}
              />
              <textarea
                data-testid="volume-ii-whats-on-mind"
                value={whatsOnMind}
                onChange={(e) => setWhatsOnMind(e.target.value)}
                placeholder="anything on your mind to bring into it? (optional)"
                maxLength={400}
                style={{ ...inputStyle, minHeight: 70, fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic", fontSize: 16, lineHeight: 1.5, marginTop: 10 }}
              />
              {readingError && <p className="error-banner" data-testid="volume-ii-reading-error" style={{ marginTop: 12 }}>{readingError}</p>}
              <button
                type="button"
                data-testid="volume-ii-submit-reading"
                onClick={submitReading}
                disabled={readingLoading || !uploadedB64}
                className="primary-btn"
                style={{ width: "100%", marginTop: 14, padding: "14px 18px" }}
              >
                {readingLoading ? "the grounds are speaking…" : `read my day ${pass.current_day} cup`}
              </button>
              <button
                type="button"
                data-testid="volume-ii-re-upload"
                onClick={() => { setImageDataUrl(null); setUploadedB64(null); setUploadedType(null); setChosenLens(null); }}
                className="mono-btn"
                style={{ width: "100%", marginTop: 8, padding: "10px 14px" }}
              >
                upload a different cup
              </button>
            </>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            capture="environment"
            style={{ display: "none" }}
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="volume-ii-file-input"
          />
        </div>
      )}

      {/* Day 28 ARC keepsake — return-visit access */}
      {pass.readings.length >= pass.total_days && (
        <div data-testid="volume-ii-arc-block" style={{ marginTop: 30 }}>
          {!arcData ? (
            <div style={{ textAlign: "center", padding: "20px 16px", background: "rgba(212, 115, 75, 0.06)", border: "1px solid rgba(212, 115, 75, 0.28)" }}>
              <p style={{ margin: "0 0 8px", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.28em", textTransform: "uppercase", color: "var(--terracotta-soft)" }}>
                <Sparkles size={11} style={{ verticalAlign: "middle", marginRight: 6 }} />
                the arc
              </p>
              <p style={{ margin: "6px 0 14px", fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontSize: 18, color: "var(--cream)", lineHeight: 1.5 }}>
                one last thing — the shape of the month, if you want to see it.
              </p>
              <button
                type="button"
                className="primary-btn"
                data-testid="volume-ii-arc-reveal-btn"
                onClick={revealArc}
                disabled={arcLoading}
                style={{ padding: "12px 20px" }}
              >
                {arcLoading ? "stitching your thread…" : "reveal your arc"}
              </button>
              {arcError && <p className="error-banner" data-testid="volume-ii-arc-error" style={{ marginTop: 12 }}>{arcError}</p>}
            </div>
          ) : (
            <div data-testid="volume-ii-arc-content" style={{ padding: "26px 22px", background: "rgba(232, 220, 199, 0.94)", color: "#0b1420", border: "1px solid rgba(212, 115, 75, 0.4)" }}>
              <p style={{ margin: 0, textAlign: "center", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.28em", textTransform: "uppercase", color: "#8a5a3f" }}>
                ✦ the arc ✦
              </p>
              <h3 data-testid="volume-ii-arc-title" style={{ margin: "10px 0 18px", fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic", fontSize: 24, textAlign: "center", lineHeight: 1.2 }}>
                {arcData.title}
              </h3>
              <div data-testid="volume-ii-arc-text" style={{ fontFamily: "'Georgia', serif", fontSize: 15, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                {arcData.arc_text}
              </div>
              {arcData.closing_line && (
                <p data-testid="volume-ii-arc-closing" style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid rgba(11, 20, 32, 0.15)", fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic", fontSize: 17, textAlign: "center", lineHeight: 1.4 }}>
                  {arcData.closing_line}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* PAST DAYS LIBRARY */}
      {pass.readings.length > 0 && (
        <div data-testid="volume-ii-library" style={{ marginTop: 30 }}>
          <p className="section-marker" style={{ marginBottom: 12 }}>
            <Calendar size={11} style={{ verticalAlign: "middle", marginRight: 6 }} />
            your journey so far
          </p>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
            {pass.readings.map((r) => (
              <li
                key={r.day}
                data-testid={`volume-ii-library-day-${r.day}`}
                style={libraryRowStyle}
              >
                <span style={{ fontFamily: "'VT323', monospace", fontSize: 22, color: "var(--terracotta-soft)", marginRight: 12 }}>
                  {String(r.day).padStart(2, "0")}
                </span>
                <p style={{ margin: 0, fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic", fontSize: 15, color: "var(--cream)", lineHeight: 1.4, flex: 1 }}>
                  {r.pull_quote || r.cup_name || "(no quote captured)"}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p style={{ margin: "32px 0 0", textAlign: "center" }}>
        <button type="button" onClick={signOut} data-testid="volume-ii-signout" className="mono-btn" style={{ padding: "10px 16px" }}>
          sign out of this device
        </button>
      </p>
    </div>
  );
}

VolumeIIPanel.TOKEN_KEY = TOKEN_KEY;

// -------- styles --------
const shellStyle = {
  maxWidth: 560,
  margin: "0 auto",
  padding: "32px 22px 64px",
  position: "relative",
  zIndex: 2,
};
const cardStyle = {
  marginTop: 24,
  padding: "22px 20px 26px",
  background: "rgba(143, 196, 224, 0.05)",
  border: "1px solid rgba(143, 196, 224, 0.22)",
};
const inputStyle = {
  width: "100%",
  padding: "11px 14px",
  background: "rgba(5, 11, 27, 0.55)",
  border: "1px solid rgba(143, 196, 224, 0.22)",
  color: "var(--cream)",
  fontFamily: "'Space Mono', monospace",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
};
const progressOuterStyle = {
  position: "relative",
  height: 4,
  background: "rgba(143, 196, 224, 0.12)",
  margin: "0 0 6px",
};
const progressInnerStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  height: "100%",
  background: "linear-gradient(90deg, var(--terracotta-soft), var(--gold))",
  transition: "width 360ms ease",
};
const progressLabelStyle = {
  position: "absolute",
  right: 0,
  top: 8,
  fontFamily: "'Space Mono', monospace",
  fontSize: 9,
  letterSpacing: "0.18em",
  color: "var(--dim)",
};
const libraryRowStyle = {
  display: "flex",
  alignItems: "center",
  padding: "8px 12px",
  background: "rgba(143, 196, 224, 0.04)",
  border: "1px solid rgba(143, 196, 224, 0.18)",
};
